import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
interface PriceTabProps {
  pricingApproach: string;
  setPricingApproach: (value: string) => void;
  pricingDescription: string;
  setPricingDescription: (value: string) => void;
}
interface PricingInputs {
  basePrice: number;
  productionCost: number;
  targetMargin: number;
  competitorPrice: number;
  marketSize: number;
  demandElasticity: number;
  timeInMarket: number;
  psychologicalThreshold: number;
  seasonalFactor: number;
  marketGrowthRate: number;
  customerAcquisitionCost: number;
  customerLifetimeValue: number;
  fixedCosts: number;
  variableCostPerUnit: number;
}
export const PriceTab: React.FC<PriceTabProps> = ({
  pricingApproach,
  setPricingApproach,
  setPricingDescription
}) => {
  const [inputs, setInputs] = useState<PricingInputs>({
    basePrice: 100,
    productionCost: 50,
    targetMargin: 30,
    competitorPrice: 120,
    marketSize: 10000,
    demandElasticity: 1.2,
    timeInMarket: 12,
    psychologicalThreshold: 99,
    seasonalFactor: 0.15,
    marketGrowthRate: 0.05,
    customerAcquisitionCost: 25,
    customerLifetimeValue: 500,
    fixedCosts: 100000,
    variableCostPerUnit: 35
  });
  const [graphData, setGraphData] = useState<any[]>([]);
  const getPricingDescription = (approach: string) => {
    switch (approach) {
      case "cost-plus":
        return "Calculate the total cost of producing the product or delivering the service and add a desired profit margin. This method ensures cost recovery and predictable margins, commonly used in manufacturing and B2B services where cost transparency is valued.";
      case "value-based":
        return "Set prices based on the perceived value to the customer rather than production costs. This approach maximizes revenue potential by aligning price with customer willingness to pay, often used for innovative products or services with clear differentiation.";
      case "competition-based":
        return "Analyze competitor pricing structures and position accordingly. This market-driven approach considers competitive dynamics, market positioning, and price elasticity to optimize market share and profitability within competitive constraints.";
      case "skimming":
        return "Launch at premium pricing to capture early adopters, then systematically reduce prices to penetrate broader market segments. This strategy maximizes revenue from price-insensitive customers before expanding market reach.";
      case "penetration":
        return "Enter market with aggressive low pricing to rapidly gain market share and establish customer base, then gradually increase prices as market position strengthens and switching costs increase.";
      case "psychological":
        return "Leverage cognitive biases and price perception psychology to optimize customer response. Uses price anchoring, charm pricing, and bundling strategies to influence purchase decisions beyond rational value assessment.";
      case "dynamic":
        return "Implement real-time price optimization based on demand fluctuations, inventory levels, competitive actions, and market conditions. Common in digital markets, travel, and industries with perishable inventory.";
      default:
        return "Select a pricing approach to see its description and recommendations.";
    }
  };

  // Industry-standard pricing model calculations
  const generateRealisticPricingData = () => {
    const data: any[] = [];
    switch (pricingApproach) {
      case "cost-plus":
        // Cost-Plus Pricing Model: P = VC + FC/Q + Margin
        for (let volume = 100; volume <= 10000; volume += 500) {
          const fixedCostPerUnit = inputs.fixedCosts / volume;
          const totalCostPerUnit = inputs.variableCostPerUnit + fixedCostPerUnit;
          const priceWithMargin = totalCostPerUnit * (1 + inputs.targetMargin / 100);
          const revenue = priceWithMargin * volume;
          const totalCosts = inputs.fixedCosts + inputs.variableCostPerUnit * volume;
          const profit = revenue - totalCosts;
          const marginPercent = profit / revenue * 100;
          data.push({
            volume: volume,
            price: priceWithMargin,
            totalCost: totalCostPerUnit,
            margin: marginPercent,
            profit: profit,
            revenue: revenue
          });
        }
        break;
      case "value-based":
        // Value-Based Pricing: Price = Customer Value - Customer Acquisition Cost
        for (let valueIndex = 1; valueIndex <= 20; valueIndex++) {
          const customerValue = inputs.customerLifetimeValue * (0.5 + valueIndex * 0.05);
          const optimalPrice = customerValue * 0.25; // Capture 25% of customer value
          const demandAtPrice = inputs.marketSize * Math.exp(-inputs.demandElasticity * (optimalPrice / inputs.basePrice - 1));
          const netCustomerValue = customerValue - optimalPrice - inputs.customerAcquisitionCost;
          const priceToValueRatio = optimalPrice / customerValue * 100;
          data.push({
            valueLevel: valueIndex,
            price: optimalPrice,
            customerValue: customerValue,
            demand: demandAtPrice,
            netValue: netCustomerValue,
            valueCapture: priceToValueRatio
          });
        }
        break;
      case "competition-based":
        // Competition-Based Pricing: Relative price positioning
        const competitorPrices = [inputs.competitorPrice * 0.85, inputs.competitorPrice * 0.95, inputs.competitorPrice, inputs.competitorPrice * 1.05, inputs.competitorPrice * 1.15];
        competitorPrices.forEach((competitorPrice, index) => {
          const relativePosition = (competitorPrice - inputs.competitorPrice) / inputs.competitorPrice * 100;
          const ourPrice = inputs.basePrice;
          const priceAdvantage = (competitorPrice - ourPrice) / competitorPrice * 100;

          // Market share based on price competitiveness (logarithmic relationship)
          const marketShare = 20 + priceAdvantage * 0.8 - Math.abs(priceAdvantage * 0.1);
          const adjustedMarketShare = Math.max(5, Math.min(60, marketShare));
          data.push({
            scenario: index + 1,
            competitorPrice: competitorPrice,
            ourPrice: ourPrice,
            priceAdvantage: priceAdvantage,
            marketShare: adjustedMarketShare,
            relativePosition: relativePosition
          });
        });
        break;
      case "penetration":
        // Penetration Pricing: Market share growth over time
        for (let month = 1; month <= 24; month++) {
          const priceReductionFactor = Math.exp(-0.1 * month); // Exponential price increase
          const penetrationPrice = inputs.basePrice * (0.7 + 0.3 * priceReductionFactor);

          // S-curve market adoption
          const adoptionRate = 1 / (1 + Math.exp(-0.8 * (month - 8)));
          const marketShare = adoptionRate * 45; // Max 45% market share

          // Revenue considers decreasing price but increasing volume
          const volume = marketShare * inputs.marketSize / 100;
          const revenue = penetrationPrice * volume;
          const costs = inputs.productionCost * volume + inputs.fixedCosts;
          const profitMargin = (revenue - costs) / revenue * 100;
          data.push({
            month: month,
            price: penetrationPrice,
            marketShare: marketShare,
            volume: volume,
            revenue: revenue,
            profitMargin: profitMargin
          });
        }
        break;
      case "skimming":
        // Price Skimming: High initial price declining over time
        for (let quarter = 1; quarter <= 16; quarter++) {
          const timeDecayFactor = Math.exp(-0.15 * quarter);
          const skimmingPrice = inputs.basePrice * (1 + timeDecayFactor * 1.5);

          // Demand increases as price decreases (price elasticity)
          const priceRatio = skimmingPrice / inputs.basePrice;
          const demandMultiplier = Math.pow(priceRatio, -inputs.demandElasticity);
          const demand = inputs.marketSize * 0.1 * demandMultiplier;
          const revenue = skimmingPrice * demand;
          const costs = inputs.productionCost * demand;
          const profitMargin = (revenue - costs) / revenue * 100;
          data.push({
            quarter: quarter,
            price: skimmingPrice,
            demand: demand,
            revenue: revenue,
            profitMargin: profitMargin,
            cumulativeDemand: demand * quarter * 0.8
          });
        }
        break;
      case "psychological":
        // Psychological Pricing: Price points and demand response
        const psychologicalPrices = [Math.floor(inputs.psychologicalThreshold) - 0.01, Math.floor(inputs.psychologicalThreshold), Math.floor(inputs.psychologicalThreshold) + 0.99, Math.floor(inputs.psychologicalThreshold * 1.1) - 0.01, Math.floor(inputs.psychologicalThreshold * 1.2) + 0.99];
        psychologicalPrices.forEach((price, index) => {
          // Psychological pricing effect on demand
          const isCharmPrice = price % 1 === 0.99 || price % 1 === 0.95;
          const psychologicalBoost = isCharmPrice ? 1.15 : 1.0;
          const priceElasticity = Math.pow(price / inputs.basePrice, -inputs.demandElasticity);
          const demand = inputs.marketSize * 0.1 * priceElasticity * psychologicalBoost;
          const revenue = price * demand;
          const costs = inputs.productionCost * demand;
          const profit = revenue - costs;
          data.push({
            pricePoint: index + 1,
            price: price,
            demand: demand,
            revenue: revenue,
            profit: profit,
            psychologicalEffect: (psychologicalBoost - 1) * 100,
            isCharmPrice: isCharmPrice
          });
        });
        break;
      case "dynamic":
        // Dynamic Pricing: Seasonal and demand-based adjustments
        for (let week = 1; week <= 52; week++) {
          // Seasonal demand curve (annual cycle)
          const seasonalMultiplier = 1 + inputs.seasonalFactor * Math.sin(2 * Math.PI * week / 52);

          // Random demand shocks (market volatility)
          const demandShock = 1 + (Math.random() - 0.5) * 0.3;
          const baseDemand = inputs.marketSize * 0.02 * seasonalMultiplier * demandShock;

          // Dynamic price adjustment based on demand
          const demandRatio = baseDemand / (inputs.marketSize * 0.02);
          const dynamicPrice = inputs.basePrice * Math.pow(demandRatio, 0.3);
          const actualDemand = baseDemand * Math.pow(dynamicPrice / inputs.basePrice, -0.8);
          const revenue = dynamicPrice * actualDemand;
          const utilization = actualDemand / (inputs.marketSize * 0.02) * 100;
          data.push({
            week: week,
            basePrice: inputs.basePrice,
            dynamicPrice: dynamicPrice,
            demand: actualDemand,
            revenue: revenue,
            utilization: utilization,
            seasonalFactor: seasonalMultiplier,
            priceMultiplier: dynamicPrice / inputs.basePrice
          });
        }
        break;
    }
    setGraphData(data);
  };
  useEffect(() => {
    generateRealisticPricingData();
  }, [pricingApproach, inputs]);
  const renderInputFields = () => {
    const getRelevantFields = () => {
      switch (pricingApproach) {
        case "cost-plus":
          return ['basePrice', 'productionCost', 'targetMargin', 'fixedCosts', 'variableCostPerUnit'];
        case "value-based":
          return ['basePrice', 'customerLifetimeValue', 'customerAcquisitionCost', 'marketSize', 'demandElasticity'];
        case "competition-based":
          return ['basePrice', 'competitorPrice', 'marketSize'];
        case "penetration":
        case "skimming":
          return ['basePrice', 'productionCost', 'marketSize', 'demandElasticity'];
        case "psychological":
          return ['psychologicalThreshold', 'basePrice', 'marketSize', 'demandElasticity'];
        case "dynamic":
          return ['basePrice', 'seasonalFactor', 'marketSize'];
        default:
          return ['basePrice', 'productionCost', 'targetMargin', 'competitorPrice'];
      }
    };
    const relevantFields = getRelevantFields();
    const fieldLabels = {
      basePrice: 'Base Price ($)',
      productionCost: 'Production Cost ($)',
      targetMargin: 'Target Margin (%)',
      competitorPrice: 'Competitor Price ($)',
      marketSize: 'Market Size (units)',
      demandElasticity: 'Demand Elasticity',
      psychologicalThreshold: 'Psychological Threshold ($)',
      seasonalFactor: 'Seasonal Factor (0-1)',
      fixedCosts: 'Fixed Costs ($)',
      variableCostPerUnit: 'Variable Cost/Unit ($)',
      customerLifetimeValue: 'Customer LTV ($)',
      customerAcquisitionCost: 'Customer CAC ($)'
    };
    return <div className="grid grid-cols-2 gap-3 py-0 my-0 mx-4">
        {relevantFields.map(field => <div key={field}>
            <Label htmlFor={field} className="text-primary-light text-[12px]">
              {fieldLabels[field as keyof typeof fieldLabels]}
            </Label>
            <Input id={field} type="number" value={inputs[field as keyof PricingInputs]} onChange={e => setInputs({
          ...inputs,
          [field]: Number(e.target.value)
        })} step={field.includes('Factor') || field.includes('Elasticity') ? 0.1 : 1} />
          </div>)}
      </div>;
  };
  const renderGraph = () => {
    if (!graphData.length) return null;
    const getChartConfig = () => {
      switch (pricingApproach) {
        case "cost-plus":
          return {
            xAxis: "volume",
            xLabel: "Production Volume (units)",
            yLabel: "Price/Cost ($)",
            lines: [{
              key: "price",
              color: "#6366F1",
              name: "Price with Margin"
            }, {
              key: "totalCost",
              color: "#EF4444",
              name: "Total Cost per Unit"
            }]
          };
        case "value-based":
          return {
            xAxis: "valueLevel",
            xLabel: "Value Perception Level",
            yLabel: "Value ($)",
            lines: [{
              key: "price",
              color: "#6366F1",
              name: "Optimal Price"
            }, {
              key: "customerValue",
              color: "#10B981",
              name: "Customer Value"
            }, {
              key: "netValue",
              color: "#F59E0B",
              name: "Net Customer Value"
            }]
          };
        case "competition-based":
          return {
            xAxis: "scenario",
            xLabel: "Competitive Scenario",
            yLabel: "Price ($) / Market Share (%)",
            lines: [{
              key: "competitorPrice",
              color: "#EF4444",
              name: "Competitor Price"
            }, {
              key: "ourPrice",
              color: "#6366F1",
              name: "Our Price"
            }, {
              key: "marketShare",
              color: "#10B981",
              name: "Market Share %"
            }]
          };
        case "penetration":
          return {
            xAxis: "month",
            xLabel: "Time (months)",
            yLabel: "Price ($) / Market Share (%)",
            lines: [{
              key: "price",
              color: "#6366F1",
              name: "Price"
            }, {
              key: "marketShare",
              color: "#10B981",
              name: "Market Share %"
            }]
          };
        case "skimming":
          return {
            xAxis: "quarter",
            xLabel: "Time (quarters)",
            yLabel: "Price ($) / Demand (units)",
            lines: [{
              key: "price",
              color: "#6366F1",
              name: "Skimming Price"
            }, {
              key: "demand",
              color: "#10B981",
              name: "Demand"
            }]
          };
        case "psychological":
          return {
            xAxis: "pricePoint",
            xLabel: "Price Point Scenario",
            yLabel: "Value ($)",
            lines: [{
              key: "price",
              color: "#6366F1",
              name: "Price"
            }, {
              key: "demand",
              color: "#10B981",
              name: "Demand"
            }, {
              key: "revenue",
              color: "#F59E0B",
              name: "Revenue"
            }]
          };
        case "dynamic":
          return {
            xAxis: "week",
            xLabel: "Time (weeks)",
            yLabel: "Price ($) / Utilization (%)",
            lines: [{
              key: "dynamicPrice",
              color: "#6366F1",
              name: "Dynamic Price"
            }, {
              key: "basePrice",
              color: "#94A3B8",
              name: "Base Price"
            }, {
              key: "utilization",
              color: "#10B981",
              name: "Utilization %"
            }]
          };
        default:
          return {
            xAxis: "x",
            xLabel: "X Axis",
            yLabel: "Y Axis",
            lines: []
          };
      }
    };
    const config = getChartConfig();
    return <ResponsiveContainer width="100%" height={440}>
        <LineChart data={graphData} margin={{
        top: 50,
        right: 20,
        left: 20,
        bottom: 50
      }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xAxis} label={{
          value: config.xLabel,
          position: 'bottom',
          offset: -10
        }} />
          <YAxis label={{
          value: config.yLabel,
          angle: -90,
          position: 'insideLeft'
        }} />
          <Tooltip formatter={(value: any, name: string) => [typeof value === 'number' ? value.toFixed(2) : value, name]} />
          <Legend />
          {config.lines.map(line => <Line key={line.key} type="monotone" dataKey={line.key} stroke={line.color} strokeWidth={2} dot={false} name={line.name} activeDot={{
          r: 6
        }} />)}
        </LineChart>
      </ResponsiveContainer>;
  };
  return <div className="flex flex-col gap-4">
      <p className="text-[12px] text-secondary-light">
        Design the right price of the product or service to the customer, considering factors like pricing strategy and
        competition.
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-6 h-[270px]">
            <h2 className="font-semibold text-base text-white">Pricing Approach</h2>
            
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-[12px] mb-2 text-primary-light">Select from menu</p>
                <div className="w-full">
                  <Select onValueChange={value => {
                  setPricingApproach(value);
                  setPricingDescription(getPricingDescription(value));
                }} value={pricingApproach}>
                    <SelectTrigger className="campaign-select-trigger">
                      <SelectValue placeholder="Choose pricing approach from dropdown" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-primary/20">
                      <SelectItem value="cost-plus" className="text-black hover:bg-gray-100">Cost-Plus Pricing</SelectItem>
                      <SelectItem value="value-based" className="text-black hover:bg-gray-100">Value-Based Pricing</SelectItem>
                      <SelectItem value="competition-based" className="text-black hover:bg-gray-100">Competition-Based Pricing</SelectItem>
                      <SelectItem value="penetration" className="text-black hover:bg-gray-100">Penetration Pricing</SelectItem>
                      <SelectItem value="skimming" className="text-black hover:bg-gray-100">Price Skimming</SelectItem>
                      <SelectItem value="psychological" className="text-black hover:bg-gray-100">Psychological Pricing</SelectItem>
                      <SelectItem value="dynamic" className="text-black hover:bg-gray-100">Dynamic Pricing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator className="bg-purple-300/20" />
              
              <p className="text-[13px] text-white">{getPricingDescription(pricingApproach)}</p>
            </div>
          </div>
          
          <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-2 h-[270px]">
            <h2 className="font-semibold text-base text-white my-0 py-0 mx-4">Pricing Inputs</h2>
            {renderInputFields()}
          </div>
        </div>
        
        <div className="lg:col-span-3">
          <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-6 h-full px-4 py-4 mx-0 my-0">
            <h2 className="font-semibold text-base text-white mb-10">Pricing Projections</h2>
            {graphData.length > 0 && renderGraph()}
          </div>
        </div>
      </div>
    </div>;
};